"use client"

import React from "react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Search, X } from "lucide-react"
import { useDestinationValueFormatter } from "@/hooks/use-destination-value-formatter"
import type { TestMappingResult, FullTestResults } from "@/types/ui"
import type { FlatMapping } from "@/types/ui"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"

interface TestResultsFullProps {
  results: FullTestResults
  allMappings: FlatMapping[]
}

export function TestResultsFull({ results, allMappings }: TestResultsFullProps) {
  const { t, n } = useTranslation()
  const [searchQuery, setSearchQuery] = React.useState("")
  const { formatDestinationValue } = useDestinationValueFormatter()
  
  // Helper function to get code table name for a destination field
  const getCodeTableName = (destinationField: string): string | null => {
    const mapping = allMappings.find(m => m.destinationField === destinationField)
    return mapping?.codeTableName || null
  }

  // Helper function to get code table value type for a destination field
  const getCodeTableValueType = (destinationField: string): number | null => {
    const mapping = allMappings.find(m => m.destinationField === destinationField)
    return mapping?.codeTableValueType ?? null
  }

  // Filter results by search query
  const filteredResults = React.useMemo(() => {
    if (!searchQuery.trim()) return results.mappings

    const query = searchQuery.toLowerCase()
    return results.mappings.filter(result => {
      const destinationMatch = result.destinationField.toLowerCase().includes(query)
      const entityMatch = result.entity.toLowerCase().includes(query)
      const sourceMatch = result.sourceField?.toLowerCase().includes(query)
      const destinationValueFormatted = formatDestinationValue(
        result.destinationValue, 
        getCodeTableName(result.destinationField), 
        getCodeTableValueType(result.destinationField) ?? undefined
      )
      const valueMatch = String(destinationValueFormatted).toLowerCase().includes(query)
      
      return destinationMatch || entityMatch || sourceMatch || valueMatch
    })
  }, [results.mappings, searchQuery, formatDestinationValue, getCodeTableName, getCodeTableValueType])

  return (
    <div className="flex flex-col h-full">
      {/* Header with search - Fixed/Always Visible */}
      <div className="flex-shrink-0 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="font-medium">{t("testing.results.title")}</h4>
          <Badge variant="secondary">
            {searchQuery
              ? t("testing.results.filteredCount", { shown: n(filteredResults.length), total: n(results.mappings.length) })
              : t((results.mappings.length === 1 ? "testing.results.countOne" : "testing.results.countOther"), { count: n(results.mappings.length) })}
          </Badge>
        </div>

        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t("testing.results.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-10"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Results Table - Only This Scrolls */}
      <div className="flex-1 border rounded-md overflow-hidden flex flex-col mt-4">
        <div className="flex-shrink-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("testing.results.destinationField")}</TableHead>
                <TableHead>{t("testing.results.entity")}</TableHead>
                <TableHead>{t("testing.results.sourceField")}</TableHead>
                <TableHead>{t("testing.results.destinationValue")}</TableHead>
              </TableRow>
            </TableHeader>
          </Table>
        </div>
        <div className="flex-1 overflow-y-auto">
          <Table>
            <TableBody>
              {filteredResults.length > 0 ? (
                filteredResults.map((result: TestMappingResult, index: number) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{result.destinationField}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{result.entity}</Badge>
                    </TableCell>
                    <TableCell>{result.sourceField}</TableCell>
                    <TableCell className="font-mono text-sm">
                      {formatDestinationValue(result.destinationValue, getCodeTableName(result.destinationField), getCodeTableValueType(result.destinationField) ?? undefined)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">
                    {searchQuery ? (
                      <>
                        {t("testing.results.noResultsFor")} "<strong>{searchQuery}</strong>"
                        <br />
                        <button 
                          onClick={() => setSearchQuery("")}
                          className="text-primary hover:underline mt-2"
                        >
                          {t("testing.results.clearSearch")}
                        </button>
                      </>
                    ) : (
                      t("testing.results.empty")
                    )}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  )
} 